import React, { useState, useEffect } from 'react'
import { useAuth } from '@/context/AuthContext'
import { useRestaurant } from '@/context/RestaurantContext'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
import { Badge } from '@/components/ui/badge'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { DateRangeModal } from '@/components/ui/DateRangeModal'
import { MobileFloatingFilter } from '@/components/ui/MobileFloatingFilter'
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog'
import { Skeleton } from '@/components/ui/skeleton'
import {
  FileText,
  Download,
  Calendar,
  DollarSign,
  ShoppingCart,
  TrendingUp,
  Filter,
  AlertTriangle,
  Loader2,
  RefreshCw,
  Eye,
  Search,
  ArrowUpDown,
  ArrowUp,
  ArrowDown,
  ChevronLeft,
  ChevronRight
} from 'lucide-react'
import { formatDate } from '@/lib/utils'
import { useCurrency } from '@/hooks/useCurrency'
import { parseDate, createDateRange, formatDateForInput } from '@/lib/dateUtils'
import { useBusinessDayDefaultRange } from '@/hooks/useBusinessDayDefaultRange'
import { ApiService } from '@/services/api'
import type { Bill, DateRange, PaginationInfo, BillsSummary } from '@/types'
import { getBillStatusVariant, getBillStatusLabel, isCancelledBill } from '@/lib/billStatus'

const PAGE_SIZE = 25
const SKELETON_ROW_COUNT = 8

const EMPTY_SUMMARY: BillsSummary = {
  totalRevenue: 0,
  totalOrders: 0,
  averageOrderValue: 0,
  completedOrders: 0,
  notSettledCount: 0,
  notSettledAmount: 0
}

const LOADING_MESSAGES = [
  'Loading sales reports...',
  'Analyzing your bills, almost there...',
  'Crunching the numbers for you...',
  'Counting every order carefully...',
  'Wide date range, this can take a moment...',
  'Still working on it, thanks for your patience...',
  'Almost done, hang in there...'
]

export default function SalesReportsScreen() {
  const { formatCurrency, currencySymbol } = useCurrency()
  const { user } = useAuth()
  const { currentRestaurant } = useRestaurant()
  const [bills, setBills] = useState<Bill[]>([])
  const [notSettledBills, setNotSettledBills] = useState<Bill[]>([])
  const [summary, setSummary] = useState<BillsSummary>(EMPTY_SUMMARY)
  const [pagination, setPagination] = useState<PaginationInfo>({ page: 1, pageSize: PAGE_SIZE, total: 0, totalPages: 1 })
  // Full-page loader (initial load + anything that changes the filtered set: date
  // range, search, status). Pagination/sort only reshuffle/repage the same set, so
  // they get a lightweight table-only skeleton instead — see `isTableLoading` below.
  const [isFullLoading, setIsFullLoading] = useState(true)
  const [isTableLoading, setIsTableLoading] = useState(false)
  const [errorScope, setErrorScope] = useState<'full' | 'table'>('full')
  const [isExporting, setIsExporting] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [searchInput, setSearchInput] = useState('')
  const [searchTerm, setSearchTerm] = useState('')
  const [statusFilter, setStatusFilter] = useState<string>('all')
  const [dateRange, setDateRange] = useBusinessDayDefaultRange()
  const [sortBy, setSortBy] = useState<'date' | 'billNumber' | 'customer' | 'total'>('date')
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc')
  const [selectedBill, setSelectedBill] = useState<Bill | null>(null)
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [loadingMessage, setLoadingMessage] = useState(LOADING_MESSAGES[0])

  // Debounce free-text search so it doesn't fire a request on every keystroke —
  // search/sort/status are now resolved server-side as part of pagination.
  useEffect(() => {
    const timer = setTimeout(() => setSearchTerm(searchInput), 400)
    return () => clearTimeout(timer)
  }, [searchInput])

  const loadBills = async (targetPage: number = pagination.page, scope: 'full' | 'table' = 'full') => {
    if (!currentRestaurant?._id) return

    setError(null)
    setErrorScope(scope)

    let messageTimer: ReturnType<typeof setInterval> | undefined
    if (scope === 'full') {
      setIsFullLoading(true)
      let messageIndex = 0
      setLoadingMessage(LOADING_MESSAGES[0])
      messageTimer = setInterval(() => {
        messageIndex = Math.min(messageIndex + 1, LOADING_MESSAGES.length - 1)
        setLoadingMessage(LOADING_MESSAGES[messageIndex])
      }, 2500)
    } else {
      setIsTableLoading(true)
    }

    try {
      const response = await ApiService.getBills(currentRestaurant._id, dateRange, undefined, {
        page: targetPage,
        pageSize: PAGE_SIZE,
        status: statusFilter,
        search: searchTerm || undefined,
        sortBy,
        sortOrder
      })
      if (response.success) {
        setBills(response.data)
        setNotSettledBills(response.notSettledBills || [])
        setSummary(response.summary || EMPTY_SUMMARY)
        setPagination(response.pagination || { page: 1, pageSize: PAGE_SIZE, total: response.data.length, totalPages: 1 })
      } else {
        setError(response.message || 'Failed to load bills')
      }
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to load bills')
    } finally {
      if (messageTimer) clearInterval(messageTimer)
      setIsFullLoading(false)
      setIsTableLoading(false)
    }
  }

  useEffect(() => {
    if (currentRestaurant?._id) {
      loadBills(1, 'full')
    }
    // Search and status change the filtered set itself (and therefore the summary
    // totals too), so they get the full-page loader, same as the date-range Apply.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentRestaurant?._id, searchTerm, statusFilter])

  const isFirstSortRender = React.useRef(true)
  useEffect(() => {
    if (isFirstSortRender.current) {
      isFirstSortRender.current = false
      return
    }
    // Sorting only reorders the already-fetched set — summary totals don't change —
    // so it's a table-only refresh, same as pagination.
    loadBills(1, 'table')
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sortBy, sortOrder])

  const handleSort = (column: 'date' | 'billNumber' | 'customer' | 'total') => {
    if (sortBy === column) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc')
    } else {
      setSortBy(column)
      setSortOrder('desc')
    }
  }

  const goToPage = (newPage: number) => {
    if (newPage < 1 || newPage > pagination.totalPages || newPage === pagination.page) return
    loadBills(newPage, 'table')
  }

  const handleViewBillItems = (bill: Bill) => {
    setSelectedBill(bill)
    setIsModalOpen(true)
  }

  const getSortIcon = (column: 'date' | 'billNumber' | 'customer' | 'total') => {
    if (sortBy !== column) {
      return <ArrowUpDown className="h-3 w-3 ml-1" />
    }
    return sortOrder === 'asc' ? <ArrowUp className="h-3 w-3 ml-1" /> : <ArrowDown className="h-3 w-3 ml-1" />
  }

  const handleDateChange = (field: 'from' | 'to', value: string) => {
    console.log(`📅 Sales Report date change - field: ${field}, value: ${value}`)
    console.log(`📅 Raw parseDate result:`, parseDate(value))

    setDateRange(prev => {
      console.log(`📅 Previous range details:`)
      console.log(`  - from: ${prev.from.toString()} (${formatDateForInput(prev.from)})`)
      console.log(`  - to: ${prev.to.toString()} (${formatDateForInput(prev.to)})`)

      const newDate = parseDate(value)
      console.log(`📅 Parsed new date: ${newDate.toString()} (${formatDateForInput(newDate)})`)

      const updatedRange = field === 'from'
        ? createDateRange(newDate, prev.to)
        : createDateRange(prev.from, newDate)

      console.log(`📅 Updated range details:`)
      console.log(`  - from: ${updatedRange.from.toString()} (${formatDateForInput(updatedRange.from)})`)
      console.log(`  - to: ${updatedRange.to.toString()} (${formatDateForInput(updatedRange.to)})`)

      return updatedRange
    })
  }

  const formatDateTime = (dateStr: string) => {
    if (!dateStr) return 'N/A'
    const date = new Date(dateStr)
    return date.toLocaleString('en-IN', {
      day: '2-digit',
      month: '2-digit', 
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      hour12: true
    })
  }

  const getPaymentType = (bill: any) => {
    // Debug logging for first few bills
    if (bills.indexOf(bill) < 3) {
      console.log('🔍 Payment Debug for bill:', bill.billNumber || bill._id)
      console.log('  paymentType:', bill.paymentType)
      console.log('  paymentDetails:', bill.paymentDetails)
      console.log('  status:', bill.status)
    }

    // Helper function to check for "Not Settled" - Cash payments with active status
    const isCashNotSettled = (type: string, billStatus: string) => {
      return type?.toLowerCase() === 'cash' && billStatus === 'active'
    }

    // Complimentary bills are on the house — flag them clearly and never as a payment method
    if ([bill.paymentType, bill.status, bill.paymentStatus].some((v: any) => typeof v === 'string' && /^\s*complimen?tor?y\s*$/i.test(v))) {
      return 'Complimentary'
    }

    // Check if paymentDetails array exists and has data
    if (bill.paymentDetails && Array.isArray(bill.paymentDetails) && bill.paymentDetails.length > 0) {
      // If multiple payment methods, show each with amount
      if (bill.paymentDetails.length > 1) {
        return bill.paymentDetails
          .map((payment: any) => {
            const type = payment.paymentType || payment.method || payment.type || 'Unknown'
            const amount = payment.amount || 0
            const displayType = isCashNotSettled(type, bill.status) ? 'Not Settled' : type
            return `${displayType} (${formatCurrency(amount)})`
          })
          .join(', ')
      }

      // Single payment method - just show the type
      const singlePayment = bill.paymentDetails[0]
      const type = singlePayment.paymentType || singlePayment.method || singlePayment.type || 'Cash'
      return isCashNotSettled(type, bill.status) ? 'Not Settled' : type
    }

    // Fallback to direct paymentType field or default
    const type = bill.paymentType || bill.paymentMethod || 'Cash'
    return isCashNotSettled(type, bill.status) ? 'Not Settled' : type
  }

  // Exports the full filtered range (not just the current page), so it issues its own
  // unpaginated request rather than reusing the page-sized `bills` state.
  const exportToCSV = async () => {
    if (!currentRestaurant?._id) return

    setIsExporting(true)
    try {
      const response = await ApiService.getBills(currentRestaurant._id, dateRange, undefined, {
        status: statusFilter,
        search: searchTerm || undefined,
        sortBy,
        sortOrder
      })
      if (!response.success) {
        setError(response.message || 'Failed to export bills')
        return
      }

      const headers = ['Bill Number', 'Date & Time', 'Customer', 'Phone', 'Captain', 'Total', 'Payment Method', 'Status']
      const rows = response.data.map(bill => [
        bill.billNumber || '',
        formatDateTime(bill.billSubmittedDate || bill.createdAt.toString()),
        bill.customerName || '',
        bill.customerPhone || '',
        bill.captainName || '',
        bill.total || 0,
        getPaymentType(bill),
        isCancelledBill(bill) ? 'cancelled' : (bill.status || bill.paymentStatus || '')
      ])

      const csvContent = [headers, ...rows]
        .map(row => row.join(','))
        .join('\n')

      const blob = new Blob([csvContent], { type: 'text/csv' })
      const url = window.URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = `sales-report-${dateRange.from.toISOString().split('T')[0]}-to-${dateRange.to.toISOString().split('T')[0]}.csv`
      document.body.appendChild(a)
      a.click()
      document.body.removeChild(a)
      window.URL.revokeObjectURL(url)
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to export bills')
    } finally {
      setIsExporting(false)
    }
  }

  // Full-page loader: initial load, date range Apply, search and status changes —
  // anything that changes which bills match. Pagination/sort stay on the table-only path.
  if (isFullLoading) {
    return (
      <div className="space-y-6">
        <div className="flex flex-col items-center justify-center py-12 space-y-3">
          <Loader2 className="h-8 w-8 animate-spin" />
          <span className="text-sm text-muted-foreground text-center px-4">{loadingMessage}</span>
        </div>
      </div>
    )
  }

  if (error && errorScope === 'full') {
    return (
      <div className="space-y-6">
        <Alert variant="destructive">
          <AlertTriangle className="h-4 w-4" />
          <AlertTitle>Unable to load sales data</AlertTitle>
          <AlertDescription>
            <p className="mb-3">{error}</p>
            <Button
              variant="outline"
              size="sm"
              onClick={() => loadBills(1, 'full')}
            >
              <RefreshCw className="h-4 w-4 mr-1" />
              Retry
            </Button>
          </AlertDescription>
        </Alert>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex-1 min-w-0">
          <h1 className="text-xl sm:text-2xl font-bold truncate">Sales Reports</h1>
          <p className="text-muted-foreground text-sm sm:text-base truncate">
            Sales data & reports
          </p>
        </div>
        <div className="flex items-center space-x-2 flex-shrink-0">
          <Button onClick={exportToCSV} variant="outline" size="icon" className="py-[10px] sm:w-auto sm:h-auto sm:px-4" disabled={isExporting}>
            {isExporting ? <Loader2 className="h-4 w-4 sm:mr-2 animate-spin" /> : <Download className="h-4 w-4 sm:mr-2" />}
            <span className="hidden sm:inline">Export CSV</span>
          </Button>
          <Button onClick={() => loadBills(pagination.page, 'full')} variant="outline" size="icon" className="py-[10px] sm:w-auto sm:h-auto sm:px-4">
            <RefreshCw className="h-4 w-4 sm:mr-2" />
            <span className="hidden sm:inline">Refresh</span>
          </Button>
        </div>
      </div>

      {/* Inline error for table-only refetches (pagination/sort) that fail after the
          initial load already succeeded — keeps filters/summary/table visible. */}
      {error && errorScope === 'table' && (
        <Alert variant="destructive">
          <AlertTriangle className="h-4 w-4" />
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      {/* Date Range and Filters - Desktop Only */}
      <Card className="hidden md:block">
        <CardHeader>
          <CardTitle className="text-base flex items-center space-x-2">
            <Filter className="h-4 w-4" />
            <span>Filters</span>
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid gap-2 sm:gap-4 grid-cols-2 lg:grid-cols-4">
            <div className="space-y-2 lg:col-span-2">
              <Label>Date Range</Label>
              <DateRangeModal
                dateRange={dateRange}
                onDateChange={handleDateChange}
                onApply={() => loadBills(1, 'full')}
                title="Sales Report Date Range"
                description="Select the date range for your sales report"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="search">Search</Label>
              <div className="relative">
                <Search className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                <Input
                  id="search"
                  placeholder="Bill number, customer..."
                  value={searchInput}
                  onChange={(e) => setSearchInput(e.target.value)}
                  className="pl-10"
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="status">Status</Label>
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Status</SelectItem>
                  <SelectItem value="completed">Completed</SelectItem>
                  <SelectItem value="pending">Pending</SelectItem>
                  <SelectItem value="cancelled">Cancelled</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2 sm:col-span-2 lg:col-span-1">
              <Label className="hidden sm:block">&nbsp;</Label>
              <Button onClick={() => loadBills(1, 'full')} className="w-full">
                Apply Filters
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Mobile Search & Filter */}
      <div className="md:hidden space-y-3">
        <div className="relative">
          <Search className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Search bill number, customer..."
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            className="pl-10"
          />
        </div>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger>
            <SelectValue placeholder="Filter by status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Status</SelectItem>
            <SelectItem value="completed">Completed</SelectItem>
            <SelectItem value="pending">Pending</SelectItem>
            <SelectItem value="cancelled">Cancelled</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Summary Statistics */}
      <div className="grid gap-2 sm:gap-4 grid-cols-2 lg:grid-cols-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Revenue</CardTitle>
            <DollarSign className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-xl sm:text-2xl font-bold">{formatCurrency(summary.totalRevenue)}</div>
            <p className="text-xs text-muted-foreground">
              From {pagination.total} orders
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Orders</CardTitle>
            <ShoppingCart className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-xl sm:text-2xl font-bold">{summary.totalOrders}</div>
            <p className="text-xs text-muted-foreground">
              {summary.completedOrders} completed
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Average Order Value</CardTitle>
            <TrendingUp className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-xl sm:text-2xl font-bold">{formatCurrency(summary.averageOrderValue)}</div>
            <p className="text-xs text-muted-foreground">
              Per order average
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Completion Rate</CardTitle>
            <Calendar className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-xl sm:text-2xl font-bold">
              {summary.totalOrders > 0 ? Math.round((summary.completedOrders / summary.totalOrders) * 100) : 0}%
            </div>
            <p className="text-xs text-muted-foreground">
              Order completion rate
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Sales Data Table */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center space-x-2">
            <FileText className="h-5 w-5" />
            <span>Sales Data</span>
          </CardTitle>
          <CardDescription>
            Detailed view of all sales transactions
          </CardDescription>
        </CardHeader>
        <CardContent>
          {/* Desktop Table View */}
          <div className="hidden lg:block">
            <div className="max-h-[600px] overflow-y-auto">
              <Table>
                <TableHeader className="sticky top-0 bg-background">
                  <TableRow>
                    <TableHead
                      className="cursor-pointer select-none hover:bg-accent/50"
                      onClick={() => handleSort('billNumber')}
                    >
                      <div className="flex items-center">
                        Bill #
                        {getSortIcon('billNumber')}
                      </div>
                    </TableHead>
                    <TableHead
                      className="cursor-pointer select-none hover:bg-accent/50"
                      onClick={() => handleSort('date')}
                    >
                      <div className="flex items-center">
                        Date & Time
                        {getSortIcon('date')}
                      </div>
                    </TableHead>
                    <TableHead
                      className="cursor-pointer select-none hover:bg-accent/50"
                      onClick={() => handleSort('customer')}
                    >
                      <div className="flex items-center">
                        Customer
                        {getSortIcon('customer')}
                      </div>
                    </TableHead>
                    <TableHead>Captain</TableHead>
                    <TableHead>Items</TableHead>
                    <TableHead>Payment Method</TableHead>
                    <TableHead
                      className="cursor-pointer select-none hover:bg-accent/50"
                      onClick={() => handleSort('total')}
                    >
                      <div className="flex items-center">
                        Total
                        {getSortIcon('total')}
                      </div>
                    </TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {isTableLoading ? (
                    Array.from({ length: SKELETON_ROW_COUNT }).map((_, i) => (
                      <TableRow key={`skeleton-${i}`}>
                        {Array.from({ length: 9 }).map((__, j) => (
                          <TableCell key={j}><Skeleton className="h-4 w-full max-w-24" /></TableCell>
                        ))}
                      </TableRow>
                    ))
                  ) : bills.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={9} className="text-center py-8">
                        <div className="flex flex-col items-center space-y-2">
                          <FileText className="h-8 w-8 text-muted-foreground" />
                          <span className="text-muted-foreground">No sales data found</span>
                          <span className="text-sm text-muted-foreground">
                            Try adjusting your filters or date range
                          </span>
                        </div>
                      </TableCell>
                    </TableRow>
                  ) : (
                    bills.map((bill: any) => (
                      <TableRow key={bill._id}>
                        <TableCell className="font-medium">
                          {bill.billNumber || `#${bill._id.slice(-6)}`}
                        </TableCell>
                        <TableCell>
                          <div className="text-sm">
                            {formatDateTime(bill.billSubmittedDate || bill.createdAt.toString())}
                          </div>
                        </TableCell>
                        <TableCell>
                          <div>
                            <div className="font-medium">{bill.customerName || 'Guest'}</div>
                            <div className="text-sm text-muted-foreground">{bill.customerPhone || 'No phone'}</div>
                          </div>
                        </TableCell>
                        <TableCell>
                          <div className="font-medium text-sm">
                            {bill.captainName || 'N/A'}
                          </div>
                        </TableCell>
                        <TableCell>
                          <div className="text-sm">
                            {bill.items?.length || 0} items
                          </div>
                        </TableCell>
                        <TableCell>
                          <Badge variant="outline" className="capitalize text-xs">
                            {getPaymentType(bill)}
                          </Badge>
                        </TableCell>
                        <TableCell className="font-semibold">
                          {formatCurrency(bill.total || 0)}
                        </TableCell>
                        <TableCell>
                          <Badge 
                            variant={getBillStatusVariant(bill)}
                          >
                            {getBillStatusLabel(bill)}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleViewBillItems(bill)}
                          >
                            <Eye className="h-3 w-3" />
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </div>
          </div>

          {/* Mobile Card View */}
          <div className="lg:hidden space-y-3">
            {isTableLoading ? (
              Array.from({ length: SKELETON_ROW_COUNT }).map((_, i) => (
                <div key={`skeleton-${i}`} className="border rounded-lg p-4 space-y-3">
                  <div className="flex items-center justify-between">
                    <Skeleton className="h-4 w-20" />
                    <Skeleton className="h-5 w-16" />
                  </div>
                  <Skeleton className="h-4 w-full" />
                  <Skeleton className="h-4 w-2/3" />
                </div>
              ))
            ) : bills.length === 0 ? (
              <div className="text-center py-8">
                <div className="flex flex-col items-center space-y-2">
                  <FileText className="h-8 w-8 text-muted-foreground" />
                  <span className="text-muted-foreground">No sales data found</span>
                  <span className="text-sm text-muted-foreground">
                    Try adjusting your filters or date range
                  </span>
                </div>
              </div>
            ) : (
              bills.map((bill: any) => (
                <div key={bill._id} className="border rounded-lg p-4 space-y-3">
                  <div className="flex items-center justify-between">
                    <div className="font-medium">
                      {bill.billNumber || `#${bill._id.slice(-6)}`}
                    </div>
                    <Badge 
                      variant={getBillStatusVariant(bill)}
                    >
                      {getBillStatusLabel(bill)}
                    </Badge>
                  </div>
                  
                  <div className="space-y-2">
                    <div className="flex justify-between text-sm">
                      <span className="text-muted-foreground">Customer:</span>
                      <span>{bill.customerName || 'Guest'}</span>
                    </div>
                    {bill.customerPhone && (
                      <div className="flex justify-between text-sm">
                        <span className="text-muted-foreground">Phone:</span>
                        <span>{bill.customerPhone}</span>
                      </div>
                    )}
                    <div className="flex justify-between text-sm">
                      <span className="text-muted-foreground">Captain:</span>
                      <span>{bill.captainName || 'N/A'}</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-muted-foreground">Items:</span>
                      <span>{bill.items?.length || 0} items</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-muted-foreground">Payment:</span>
                      <Badge variant="outline" className="capitalize text-xs">
                        {getPaymentType(bill)}
                      </Badge>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-muted-foreground">Date:</span>
                      <span className="text-xs">
                        {formatDateTime(bill.billSubmittedDate || bill.createdAt.toString())}
                      </span>
                    </div>
                  </div>
                  
                  <div className="flex items-center justify-between pt-2 border-t">
                    <div className="font-semibold text-lg">
                      {formatCurrency(bill.total || 0)}
                    </div>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleViewBillItems(bill)}
                    >
                      <Eye className="h-3 w-3 mr-1" />
                      View
                    </Button>
                  </div>
                </div>
              ))
            )}
          </div>

          {/* Pagination */}
          {pagination.totalPages > 1 && (
            <div className="flex items-center justify-between pt-4 mt-4 border-t">
              <span className="text-sm text-muted-foreground">
                Page {pagination.page} of {pagination.totalPages} • {pagination.total} total
              </span>
              <div className="flex items-center space-x-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => goToPage(pagination.page - 1)}
                  disabled={pagination.page <= 1 || isTableLoading}
                >
                  <ChevronLeft className="h-4 w-4 mr-1" />
                  Prev
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => goToPage(pagination.page + 1)}
                  disabled={pagination.page >= pagination.totalPages || isTableLoading}
                >
                  Next
                  <ChevronRight className="h-4 w-4 ml-1" />
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Unpaid Bills */}
      {summary.notSettledCount > 0 && (
        <Card className="border-orange-200 bg-orange-50/50 dark:bg-orange-950/20 dark:border-orange-800">
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle className="flex items-center space-x-2 text-orange-700 dark:text-orange-400">
                <AlertTriangle className="h-5 w-5" />
                <span>Unpaid Bills</span>
              </CardTitle>
              <div className="flex items-center space-x-3">
                <span className="text-sm font-medium text-orange-700 dark:text-orange-400">
                  Total: {formatCurrency(summary.notSettledAmount)}
                </span>
                <Badge variant="destructive">{summary.notSettledCount} unpaid</Badge>
              </div>
            </div>
            <CardDescription>Bills still open / not yet settled</CardDescription>
          </CardHeader>
          <CardContent>
            {/* Desktop Table */}
            <div className="hidden lg:block">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Bill #</TableHead>
                    <TableHead>Date & Time</TableHead>
                    <TableHead>Customer</TableHead>
                    <TableHead>Captain</TableHead>
                    <TableHead>Items</TableHead>
                    <TableHead>Payment Method</TableHead>
                    <TableHead className="text-right">Amount</TableHead>
                    <TableHead>Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {notSettledBills.map((bill: any) => (
                    <TableRow key={bill._id} className="bg-orange-50/30 dark:bg-orange-950/10">
                      <TableCell className="font-medium">
                        {bill.billNumber || `#${bill._id.slice(-6)}`}
                      </TableCell>
                      <TableCell className="text-sm">
                        {formatDateTime(bill.billSubmittedDate || bill.createdAt?.toString())}
                      </TableCell>
                      <TableCell>
                        <div className="font-medium">{bill.customerName || 'Guest'}</div>
                        <div className="text-xs text-muted-foreground">{bill.customerPhone || ''}</div>
                      </TableCell>
                      <TableCell className="text-sm">{bill.captainName || 'N/A'}</TableCell>
                      <TableCell className="text-sm">{bill.items?.length || 0} items</TableCell>
                      <TableCell>
                        <Badge variant="outline" className="capitalize text-xs">
                          {getPaymentType(bill)}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-right font-semibold text-orange-700 dark:text-orange-400">
                        {formatCurrency(bill.total || 0)}
                      </TableCell>
                      <TableCell>
                        <Button variant="outline" size="sm" onClick={() => handleViewBillItems(bill)}>
                          <Eye className="h-3 w-3" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>

            {/* Mobile Cards */}
            <div className="lg:hidden space-y-3">
              {notSettledBills.map((bill: any) => (
                <div key={bill._id} className="border border-orange-200 dark:border-orange-800 rounded-lg p-4 space-y-2 bg-white dark:bg-background">
                  <div className="flex items-center justify-between">
                    <span className="font-medium">{bill.billNumber || `#${bill._id.slice(-6)}`}</span>
                    <Badge variant="destructive" className="text-xs">Unpaid</Badge>
                  </div>
                  <div className="space-y-1 text-sm">
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Customer:</span>
                      <span>{bill.customerName || 'Guest'}</span>
                    </div>
                    {bill.customerPhone && (
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Phone:</span>
                        <span>{bill.customerPhone}</span>
                      </div>
                    )}
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Captain:</span>
                      <span>{bill.captainName || 'N/A'}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Items:</span>
                      <span>{bill.items?.length || 0} items</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Date:</span>
                      <span className="text-xs">{formatDateTime(bill.billSubmittedDate || bill.createdAt?.toString())}</span>
                    </div>
                  </div>
                  <div className="flex items-center justify-between pt-2 border-t">
                    <span className="font-semibold text-orange-700 dark:text-orange-400 text-lg">
                      {formatCurrency(bill.total || 0)}
                    </span>
                    <Button variant="outline" size="sm" onClick={() => handleViewBillItems(bill)}>
                      <Eye className="h-3 w-3 mr-1" />
                      View
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Mobile Floating Filter */}
      <MobileFloatingFilter
        dateRange={dateRange}
        onDateChange={handleDateChange}
        onApply={() => loadBills(1, 'full')}
        title="Sales Reports Date Range"
        description="Select the date range for your sales report"
      />

      {/* Bill Items Modal */}
      <Dialog open={isModalOpen} onOpenChange={setIsModalOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>
              Order Items - {selectedBill?.billNumber || 'N/A'}
            </DialogTitle>
            <DialogDescription>
              {selectedBill?.customerName && `Customer: ${selectedBill.customerName}`}
              {selectedBill?.billSubmittedDate && ` | ${formatDateTime(selectedBill.billSubmittedDate)}`}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            {/* Items Table */}
            <div className="border rounded-lg overflow-hidden">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Item</TableHead>
                    <TableHead className="text-center">Quantity</TableHead>
                    <TableHead className="text-right">Price</TableHead>
                    <TableHead className="text-right">Total</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {selectedBill?.items && selectedBill.items.length > 0 ? (
                    selectedBill.items.map((item, index) => (
                      <TableRow key={item._id || index}>
                        <TableCell>
                          <div>
                            <div className="font-medium">{item.name}</div>
                            {item.category && (
                              <div className="text-xs text-muted-foreground">
                                {item.category}
                              </div>
                            )}
                          </div>
                        </TableCell>
                        <TableCell className="text-center font-medium">
                          {item.quantity}
                        </TableCell>
                        <TableCell className="text-right">
                          {formatCurrency(item.price)}
                        </TableCell>
                        <TableCell className="text-right font-medium">
                          {formatCurrency(item.price * item.quantity)}
                        </TableCell>
                      </TableRow>
                    ))
                  ) : (
                    <TableRow>
                      <TableCell colSpan={4} className="text-center py-4 text-muted-foreground">
                        No items found
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </div>

            {/* Bill Summary */}
            {selectedBill && (
              <div className="space-y-2 border-t pt-4">
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">Subtotal:</span>
                  <span>{formatCurrency(selectedBill.subTotal || 0)}</span>
                </div>
                {selectedBill.discountAmount > 0 && (
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">
                      Discount ({selectedBill.discountPercentage}%):
                    </span>
                    <span className="text-red-500">
                      -{formatCurrency(selectedBill.discountAmount)}
                    </span>
                  </div>
                )}
                {selectedBill.serviceCharge > 0 && (
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">
                      Service Charge ({selectedBill.scPercentage}%):
                    </span>
                    <span>{formatCurrency(selectedBill.serviceCharge)}</span>
                  </div>
                )}
                {selectedBill.gst > 0 && (
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">
                      GST ({selectedBill.gstPercentage}%):
                    </span>
                    <span>{formatCurrency(selectedBill.gst)}</span>
                  </div>
                )}
                {selectedBill.parcelCharge > 0 && (
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">Parcel Charge:</span>
                    <span>{formatCurrency(selectedBill.parcelCharge)}</span>
                  </div>
                )}
                <div className="flex justify-between font-semibold text-lg pt-2 border-t">
                  <span>Total:</span>
                  <span>{formatCurrency(selectedBill.total || 0)}</span>
                </div>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  )
}